<script setup lang="ts" >
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useFormStore } from '@/stores/form'
import { useUserStore } from '@/stores/user';

const formStore = useFormStore()
const userStore = useUserStore()


//e962b6e10b80e9bc6fd1b8e7c396f5280e6b2ed294e45d611c1780dcc05742f9
// 系统配置
const ZOOM_CONFIG = {
  MIN: 1,
  MAX: 4,
  DURATION: 1000
}

// DOM引用
const videoElement = ref<HTMLVideoElement | null>(null)
const canvasElement = ref<HTMLCanvasElement | null>(null)
const fileNameInput = ref<HTMLInputElement | null>(null)

// 状态控制
const videoDevices = ref<MediaDeviceInfo[]>([])
const selectedDevice = ref('')
const fileName = ref('')
const VideoTitle = ref('')

const platform = ref('')
const ior = ref('')


const EnterCount = ref(0)
const isRecording = ref(false)
const isTesting = ref(false)
const showFrame = ref(false)
const zoomLevel = ref(ZOOM_CONFIG.MIN)
let mediaStream: MediaStream | null = null
let mediaRecorder: MediaRecorder | null = null
let recordedChunks: Blob[] = []
let ctx: CanvasRenderingContext2D | null = null
let videoTrack: MediaStreamTrack | null = null
let animationFrameId: number | null = null
let currentFileName = ''
let translateX = 0
let translateY = 0
let isCanceling = false



const status = ref("尚未連線");

const nickname = ref('')
const enddate = ref('')

// 登出函數
const doLogout = async () => {
  
  await userStore.logout(userStore.account)
  formStore.switchForm('login')
}

const saveencode = computed(() => {
  switch (platform.value) {
    case '蝦皮':
      return 'AA'
    case '奇摩':
      return 'BB'
    case '露天':
      return 'CC'
    case 'momo':
      return 'DD'
    case '樂天':
      return 'EE'
    case '好賣+':
      return 'FF'
    case '賣貨便':
      return 'GG'
    case 'iOPEN Mall':
      return 'HH'
      case '官方':
      return 'II'  
    default:
      return ''
  }
})



const iorcode = computed(() => {
  switch (ior.value) {
    case '銷售':
      return 'O'
    case '進貨':
      return 'I'
    case '退貨':
      return 'R'
  }
})

// 修改後的初始化摄像头函數
const initCamera = async () => {

  try {
    // 先測試基本權限
    const baseStream = await navigator.mediaDevices.getUserMedia({ video: true })
    baseStream.getTracks().forEach(t => t.stop())

    // 獲取所有視訊設備
    const devices = await navigator.mediaDevices.enumerateDevices()
      const videoDevicesList = devices.filter(d => d.kind === 'videoinput')
    
    if (videoDevicesList.length === 0) {
      throw new Error('未找到可用鏡頭，請登出')
    }

    
    //const usbCamera = videoDevicesList.find(d => d.label.includes('USB 2.0 Camera (0806:0806)'))
    //const usbCamera = videoDevicesList.find(d => d.label.includes('HB-HD Camera'))
    
    const usbCamera = videoDevicesList.find(d =>
      ['HB-HD Camera','HB Camera','USB 2.0 Camera 4K','ULtra HD 4K - 09'].some(name => d.label.includes(name))
    )
    
    if (!usbCamera) {
      throw new Error('未找到可用鏡頭，請登出')
    }

    // 更新 videoDevices 以供未來使用（例如 UI 選擇）
    videoDevices.value = videoDevicesList
    // 選擇第一個可用設備
    selectedDevice.value = videoDevicesList[0].deviceId
    console.log('選定的攝影機設備 ID:', selectedDevice.value)

    // 獲取高解析度流
    mediaStream = await navigator.mediaDevices.getUserMedia({
      video: {
        deviceId: { exact: selectedDevice.value },
        // width: { ideal: 1920 },
        // height: { ideal: 1080 }
        // width: { ideal: 2560 },
        // height: { ideal: 1440 }
        width: { ideal: 3840 },
        height: { ideal: 2160 }
      }
    })

    if (videoElement.value) {
      videoElement.value.srcObject = mediaStream
      videoTrack = mediaStream.getVideoTracks()[0]
      setupCanvas()
      checkZoomCapability()
    }
  } catch (error) {
    console.error('鏡頭初始化失敗:', error)
    const ansy = confirm('鏡頭無法使用，請查看連線或未找到可用鏡頭，將自動登出')
    if ( ansy ){
      doLogout() 
    }else{
      doLogout() 
    }   
  }
}

// 检查缩放能力
const checkZoomCapability = () => {
  const capabilities = videoTrack?.getCapabilities();
  if (capabilities && 'zoom' in capabilities) {
    // const caps = videoTrack.getCapabilities().zoom
    const caps = 'zoom' in capabilities ? (capabilities as any).zoom : undefined;
    ZOOM_CONFIG.MIN = caps.min
    ZOOM_CONFIG.MAX = caps.max
  }
}

// 设置Canvas
const setupCanvas = () => {
  if (!canvasElement.value || !videoElement.value) return

  const updateCanvasSize = () => {
    canvasElement.value!.width = videoElement.value!.videoWidth
    canvasElement.value!.height = videoElement.value!.videoHeight
  }

  videoElement.value.onresize = updateCanvasSize
  updateCanvasSize()

  ctx = canvasElement.value.getContext('2d', { alpha: false })
  drawFrame()
}

// 绘制画面（修改后）
const drawFrame = () => {
  if (!ctx || !videoElement.value || !canvasElement.value) return

  ctx.save()
  ctx.clearRect(0, 0, canvasElement.value.width, canvasElement.value.height)

  // 应用平移和缩放
  ctx.translate(translateX, translateY)
  ctx.scale(zoomLevel.value, zoomLevel.value)
  ctx.drawImage(videoElement.value, 0, 0)

  ctx.restore() // 恢复状态以确保覆盖层不受变换影响

  // 绘制覆盖层
  drawOverlay()
  
  animationFrameId = requestAnimationFrame(drawFrame)
}

// 绘制覆盖层
const drawOverlay = () => {
  if (!ctx || !canvasElement.value) return
  
  const time = new Date().toLocaleString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
  ctx.fillStyle = '#ff1493'
  ctx.font = 'bold 42px Arial'
  ctx.fillText(time, 40, 150)

  if (isRecording.value && currentFileName) {
    ctx.font = 'bold 42px Arial'
    ctx.fillText('[虔智科技影像認證第三方平台]',40,100)
    ctx.fillText('平台:'+ platform.value ,40,200)
    ctx.fillText('作業模式:'+ior.value ,40,250)
    ctx.fillText('用戶標籤:' + VideoTitle.value, 40, 300)
    ctx.fillText('條碼:' + currentFileName, 40, 350)
  }
  //九宮格
  //ctx.strokeStyle = '#00FF00'; // 線條顏色（可調整）
  //ctx.strokeStyle = 'rgba(0, 255, 0, 0.5)' // 半透明線
  ctx.strokeStyle = 'rgba(255, 20, 147, 0.8)' // 半透明線
  ctx.lineWidth = 5;           // 線條寬度（可調整）
  ctx.setLineDash([20, 40]);     // 虛線樣式（也可改成實線 ctx.setLineDash([])）

  let width = 3840
  let height = 2160
  
  // 垂直線（2 條）
  const colWidth = width / 3;
  for (let i = 1; i < 3; i++) {
    ctx.beginPath();
    ctx.moveTo(colWidth * i, 0);
    ctx.lineTo(colWidth * i, height);
    ctx.stroke();
  }

  // 水平線（2 條）
  const rowHeight = height / 3;
  for (let i = 1; i < 3; i++) {
    ctx.beginPath();
    ctx.moveTo(0, rowHeight * i);
    ctx.lineTo(width, rowHeight * i);
    ctx.stroke();
  }

  // 如果需要恢復為實線畫其他圖形
  ctx.setLineDash([]);
  //九宮格
  // if (showFrame.value) {
  //   ctx.strokeStyle = '#ff1493'
  //   ctx.setLineDash([10, 5])
  //   ctx.lineWidth = 3
  //   ctx.strokeRect(550, 350, 850, 550)
  // }
}

const smoothZoom = async (target: number) => {
  return new Promise(resolve => {
    const startScale = zoomLevel.value
    const startTime = Date.now()
    
    // 获取canvas元素
    const canvas = canvasElement.value
    if (!canvas) return resolve(undefined);

    // 视口中心坐标（相对于canvas）
    const viewportCenter = {
      x: canvas.width / 2,
      y: canvas.height / 2
    }

    // 计算当前视口中心对应的世界坐标（考虑当前平移和缩放）
    const worldCenter = {
      x: (viewportCenter.x - translateX) / startScale,
      y: (viewportCenter.y - translateY) / startScale
    }

    const animate = () => {
      const progress = Math.min((Date.now() - startTime) / ZOOM_CONFIG.DURATION, 1)
      const newScale = startScale + (target - startScale) * progress
      
      // 计算新的平移量以保持世界坐标在视口中心
      translateX = viewportCenter.x - worldCenter.x * newScale
      translateY = viewportCenter.y - worldCenter.y * newScale
      zoomLevel.value = newScale
      
      // 更新摄像头硬件缩放（如果需要）
      applyZoom()
      
      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        resolve(undefined)
      }
    }
    animate()
  })
}

// 移除CSS变换相关的代码
const applyZoom = async () => {
  if (!videoTrack) return
  try {
    if (videoTrack.getCapabilities().zoom) {
      await videoTrack.applyConstraints({
        advanced: [{
          zoom: Math.min(Math.max(zoomLevel.value, ZOOM_CONFIG.MIN), ZOOM_CONFIG.MAX)
        }]
      })
    }
  } catch (error) {
    console.error('Zoom constraint apply failed:', error)
  }
}


const startRecording = () => {
  if (!fileName.value.trim()) return alert('無效的檔案名稱,不能是空值')
  if (!canvasElement.value) return

  currentFileName = fileName.value
  isRecording.value = true
  recordedChunks = []
  isCanceling = false // reset flag

  const canvasStream = canvasElement.value.captureStream(30)
  mediaRecorder = new MediaRecorder(canvasStream)

  mediaRecorder.ondataavailable = e => {
    if (e.data.size > 0) recordedChunks.push(e.data)
  }

  mediaRecorder.onstop = () => {
    if (isCanceling) {
      recordedChunks = []
      return // 不下載
    }

    const blob = new Blob(recordedChunks, { type: 'video/webm' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${currentFileName + iorcode.value + saveencode.value}.webm`
    a.click()
    URL.revokeObjectURL(url)
    currentFileName = ''
  }

  mediaRecorder.start(1000)
}

const stopRecording = () => {
  if (mediaRecorder && isRecording.value) {
    isCanceling = false
    mediaRecorder.stop()
    mediaRecorder = null
    isRecording.value = false
    fileName.value = ''
  }
}

const cancelRecording = () => {
  if (mediaRecorder && isRecording.value) {
    isCanceling = true
    mediaRecorder.stop()
    mediaRecorder = null
    isRecording.value = false
    fileName.value = ''
  }
}



const runTest = async () => {
  if (isTesting.value) return
  isTesting.value = true

  await new Promise(resolve => setTimeout(resolve, 3000))
  await smoothZoom(2)

  for (let i = 0; i < 2; i++) {
    showFrame.value = true
    await new Promise(resolve => setTimeout(resolve, 2000))
    showFrame.value = false
    await new Promise(resolve => setTimeout(resolve, 2000))
  }

  await smoothZoom(1)
  isTesting.value = false
}

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}
//keyevent
const handleKeyDown = async (e: KeyboardEvent) => {

  if ( userStore.isLoggedIn ===false )
    return

  let message = ""

  if (platform.value === "") {
    message += "請選定電商名稱\n"
  }

  if (ior.value === "") {
    message += "請選定作業模式"
  }

  if (message !== "") {
    alert(message)
    return
  }

  if (e.key === 'Enter') {    
    e.stopPropagation()
    e.preventDefault()
    EnterCount.value += 1
    if (!isRecording.value) {

      if ( await userStore.checkToken() ==false ){
        doLogout() 
        
      }

      startRecording()

      

      //20250624 begin      
      if ( ior.value === "退貨" || ior.value === "進貨" ){
        smoothZoom(3)
        showFrame.value = true
        await sleep(4000)
        showFrame.value = false
        smoothZoom(1)      
        await sleep(2000)    
      }
      //20250624 end

    } else if (EnterCount.value === 2) {
      

      if ( ior.value === "銷售" ){
        smoothZoom(3)
        showFrame.value = true
        await sleep(4000)
        showFrame.value = false
        smoothZoom(1)      
        await sleep(2000)   
      }
      
      // smoothZoom(3)
      // showFrame.value = true
      // await sleep(4000)
      // showFrame.value = false
      // smoothZoom(1)      
      // await sleep(2000)   
      await sleep(3000)   
      stopRecording()
      EnterCount.value = 0
      
      fileName.value = ''
    }
  }

  if (e.key === 'Escape') {
    e.stopPropagation()
    e.preventDefault()
    showFrame.value = false
    cancelRecording() // 改成取消錄影（不下載）
  
    if (isTesting.value) isTesting.value = false

    EnterCount.value = 0
    fileName.value = ''
  }
}

// init
onMounted(async () => {
  nickname.value = userStore.nickname
  enddate.value = userStore.enddate
  try {
    
    EnterCount.value = 0
    const devices = await navigator.mediaDevices.enumerateDevices()
    videoDevices.value = devices.filter(d => d.kind === 'videoinput')
    if (videoDevices.value.length) {
      await initCamera()
    } else {
      console.error('未找到視訊設備')
      alert('未檢測到攝影機，請檢查設備連接')
    }
    document.addEventListener('keydown', handleKeyDown)
  } catch (error) {
    console.error('初始化失敗:', error)
  }
})

onUnmounted(async () => {
  // await userStore.logout(userStore.account)
  // document.removeEventListener('keydown', handleKeyDown)
  mediaStream?.getTracks().forEach(t => t.stop())
  if (animationFrameId) cancelAnimationFrame(animationFrameId)
  if (mediaRecorder) mediaRecorder.stop()
  })
</script>


<template>
  
  <!-- <h1>錄影<span v-if="isRecording" class="flashing-light">REC...</span></h1> -->
  <h1>錄影</h1>
  <p>暱稱：{{ nickname }}</p>
  <p>到期日：{{ enddate }}</p>
  <div class="dVideo">
    <div class="control-panel">
      <div class="input-group">

            <!-- saveencode -->
         <div> <h3><span v-if="isRecording" class="flashing-light">REC...</span></h3> </div>
        
        <select v-model="platform">
          <option disabled value="">電商名稱</option>
          <option>蝦皮</option>
          <option>奇摩</option>
          <option>露天</option>
          <option>momo</option>
          <option>樂天</option>
          <option>好賣+</option>
          <option>賣貨便</option>
          <option>iOPEN Mall</option>
          <option>官方</option>
        </select>    


        <select v-model="ior">
          <option disabled value="">作業模式</option>
          <option>銷售</option>
          <option>進貨</option>
          <option>退貨</option>

        </select>    



        <input v-model="VideoTitle" placeholder="標籤輸入"/>
        <input
          ref="fileNameInput"
          v-model="fileName"
          placeholder="輸入條碼(編號)後按Enter"
          @keydown="handleKeyDown"
        >
      </div>
    </div>

    <div class="video-container">
      <video ref="videoElement" autoplay hidden></video>
      <canvas id="canvasx" ref="canvasElement"></canvas>
    </div>

    <button @click="doLogout">登出</button>
  </div>
</template>

<style scoped>
.dVideo {
  max-width: 1920px;
  max-height: 1080px;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  margin: auto;
  display: flex;
  flex-direction: column;
  background-color: black;
  border: 2px solid #ff1493;
  padding: 20px;
}

.flashing-light {
  width: 20px;
  height: 20px;
  background-color: rgb(255, 20, 20);
  color: #ffffff;
  border-radius: 50%;
  margin-left: 10px;
  animation: flash 1s infinite;
  font-size: 16px;
  /* font-size: medium; */
}

.control-panel {
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
}
select{
  border-radius: 10px;
  background-color: black;
  color: white;
  font-size: 18px;
  margin: 10px;
  width: 150px;
  padding: 5px;
}
input  {
  border-radius: 10px;
  background-color: black;
  color: white;
  font-size: 18px;
  margin: 10px;
  width: 250px;
  padding: 5px;
}

button {
  margin: 10px;
  border-radius: 10px;
  border: none;
  color: white;
  font-size: 20px;
  background-color: deeppink;
  width: 400px;
  padding: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;
}

button:hover {
  transform: scale(1.04);
}

button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.video-container {
  position: relative;
  border-radius: 1rem;
  overflow: hidden;
  background: #000;
}

canvas {
  width: 100%;
  height: 640px;
  display: block;
  /* 移除transform属性 */
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

@keyframes flash {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>